import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsAPI, settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save, User, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';

const ClientForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    date_of_birth: '',
    age_display: '',
    school: '',
    year_of_school: '',
    assessment_type: '',
    assessment_focus: '',
    initial_or_review: 'Initial',
    referrer: '',
    current_supports: '',
    notes: '',
    supervision_needed: false,
    payment_status: 'Not Paid',
    scheduled_date: '',
    report_due_date: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const settingsRes = await settingsAPI.get();
      setSettings(settingsRes.data);
      
      if (isEdit) {
        const clientRes = await clientsAPI.getOne(id);
        const client = clientRes.data;
        setFormData({
          name: client.name || '',
          email: client.email || '',
          date_of_birth: client.date_of_birth || '',
          age_display: client.age_display || '',
          school: client.school || '',
          year_of_school: client.year_of_school || '',
          assessment_type: client.assessment_type || '',
          assessment_focus: client.assessment_focus || '',
          initial_or_review: client.initial_or_review || 'Initial',
          referrer: client.referrer || '',
          current_supports: client.current_supports || '',
          notes: client.notes || '',
          supervision_needed: client.supervision_needed || false,
          payment_status: client.payment_status || 'Not Paid',
          scheduled_date: client.scheduled_date || '',
          report_due_date: client.report_due_date || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load data');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return '';
    try {
      const dobDate = new Date(dob);
      const today = new Date();
      
      let years = today.getFullYear() - dobDate.getFullYear();
      let months = today.getMonth() - dobDate.getMonth();
      
      if (today.getMonth() < dobDate.getMonth() || 
          (today.getMonth() === dobDate.getMonth() && today.getDate() < dobDate.getDate())) {
        years--;
        months += 12;
      }
      
      if (today.getDate() < dobDate.getDate()) {
        months--;
        if (months < 0) months += 12;
      }
      
      if (years === 0) {
        return `${months} month${months !== 1 ? 's' : ''}`;
      } else if (months === 0) {
        return `${years} year${years !== 1 ? 's' : ''}`;
      } else {
        return `${years} year${years !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''}`;
      }
    } catch {
      return '';
    }
  };

  const handleChange = (field, value) => {
    if (field === 'date_of_birth') {
      // Auto-calculate age when DOB changes
      const age = calculateAge(value);
      setFormData(prev => ({ ...prev, [field]: value, age_display: age }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }
    if (!formData.assessment_type) {
      toast.error('Assessment type is required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        year_of_school: formData.year_of_school ? parseInt(formData.year_of_school) : null
      };

      if (isEdit) {
        await clientsAPI.update(id, data);
        toast.success('Client updated');
      } else {
        const response = await clientsAPI.create(data);
        toast.success('Client created');
        navigate(`/clients/${response.data.id}`);
        return;
      }
      navigate(`/clients/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  const activeTypes = settings?.assessment_types?.filter(t => t.active) || [];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="client-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(isEdit ? `/clients/${id}` : '/clients')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope']">
            {isEdit ? 'Edit Client' : 'New Client'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isEdit ? 'Update client information' : 'Add a new client to your caseload'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
              <User className="w-5 h-5" />
              Client Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Name & Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Last, First"
                  required
                  data-testid="client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="client@example.com"
                  data-testid="client-email-input"
                />
              </div>
            </div>

            {/* Age & DOB */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => handleChange('date_of_birth', e.target.value)}
                  data-testid="client-dob-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="age_display">Age (auto-calculated)</Label>
                <Input
                  id="age_display"
                  value={formData.age_display}
                  placeholder="Enter date of birth to calculate"
                  readOnly
                  className="bg-slate-50 cursor-not-allowed"
                  data-testid="client-age-input"
                />
              </div>
            </div>

            {/* School */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="school">School</Label>
                <Input
                  id="school"
                  value={formData.school}
                  onChange={(e) => handleChange('school', e.target.value)}
                  placeholder="School name"
                  data-testid="client-school-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="year_of_school">Year of School</Label>
                <Input
                  id="year_of_school"
                  type="number"
                  min="0"
                  max="12"
                  value={formData.year_of_school}
                  onChange={(e) => handleChange('year_of_school', e.target.value)}
                  placeholder="e.g., 4"
                  data-testid="client-year-input"
                />
              </div>
            </div>

            {/* Assessment Type & Focus */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Assessment Type *</Label>
                <Select 
                  value={formData.assessment_type} 
                  onValueChange={(val) => handleChange('assessment_type', val)}
                >
                  <SelectTrigger data-testid="assessment-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeTypes.map(type => (
                      <SelectItem key={type.name} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="assessment_focus">Assessment Focus</Label>
                <Input
                  id="assessment_focus"
                  value={formData.assessment_focus}
                  onChange={(e) => handleChange('assessment_focus', e.target.value)}
                  placeholder="e.g., EF/A"
                  data-testid="assessment-focus-input"
                />
              </div>
            </div>

            {/* Key Dates - Optional Deadlines */}
            <div className="border border-dashed border-slate-300 rounded-lg p-4 bg-white">
              <h3 className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                External Deadlines (optional)
              </h3>
              <p className="text-xs text-slate-500 mb-4">Set these if there are specific deadlines you need to meet</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="scheduled_date">Assessment Required By</Label>
                  <Input
                    id="scheduled_date"
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => handleChange('scheduled_date', e.target.value)}
                    data-testid="scheduled-date-input"
                  />
                  <p className="text-xs text-slate-500">e.g., School or funding deadline</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="report_due_date">Report Required By</Label>
                  <Input
                    id="report_due_date"
                    type="date"
                    value={formData.report_due_date}
                    onChange={(e) => handleChange('report_due_date', e.target.value)}
                    data-testid="report-due-date-input"
                  />
                  <p className="text-xs text-slate-500">e.g., School meeting, NDIS review</p>
                </div>
              </div>
            </div>

            {/* Initial/Review & Referrer */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Initial or Review</Label>
                <Select 
                  value={formData.initial_or_review} 
                  onValueChange={(val) => handleChange('initial_or_review', val)}
                >
                  <SelectTrigger data-testid="initial-review-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Initial">Initial</SelectItem>
                    <SelectItem value="Review">Review</SelectItem>
                    <SelectItem value="SEA">SEA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="referrer">Referrer</Label>
                <Input
                  id="referrer"
                  value={formData.referrer}
                  onChange={(e) => handleChange('referrer', e.target.value)}
                  placeholder="e.g., Parents, Dr. Smith"
                  data-testid="referrer-input"
                />
              </div>
            </div>

            {/* Current Supports */}
            <div className="space-y-2">
              <Label htmlFor="current_supports">Current Supports</Label>
              <Textarea
                id="current_supports"
                value={formData.current_supports}
                onChange={(e) => handleChange('current_supports', e.target.value)}
                placeholder="Describe current supports and interventions..."
                className="min-h-20"
                data-testid="current-supports-textarea"
              />
            </div>

            {/* Payment & Supervision */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Status</Label>
                <Select 
                  value={formData.payment_status} 
                  onValueChange={(val) => handleChange('payment_status', val)}
                >
                  <SelectTrigger data-testid="payment-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Paid">Paid</SelectItem>
                    <SelectItem value="Not Paid">Not Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Supervision Needed</Label>
                <div className="flex items-center space-x-2 pt-2">
                  <Switch
                    id="supervision"
                    checked={formData.supervision_needed}
                    onCheckedChange={(val) => handleChange('supervision_needed', val)}
                    data-testid="supervision-switch"
                  />
                  <Label htmlFor="supervision" className="font-normal">
                    {formData.supervision_needed ? 'Yes' : 'No'}
                  </Label>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Additional notes..."
                className="min-h-24"
                data-testid="notes-textarea"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => navigate(isEdit ? `/clients/${id}` : '/clients')}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-slate-800 hover:bg-slate-900"
                disabled={saving}
                data-testid="save-client-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : (isEdit ? 'Update Client' : 'Create Client')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default ClientForm;
